import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { Select, Input } from 'components/ui';
import { SUGGEST_HSN } from 'api/graphql/catalog';

/** Total GST % for an HSN entry (IGST, or CGST + SGST). */
function gstRate(hsn) {
  if (!hsn) return null;
  if (hsn.igstRate != null) return hsn.igstRate;
  const cgst = Number(hsn.cgstRate) || 0;
  const sgst = Number(hsn.sgstRate) || 0;
  return cgst + sgst;
}

/**
 * HSN code picker driven by the selected subcategory. Lists the suggested code
 * + candidate codes (with their GST rate), auto-selecting the suggested one when
 * nothing is chosen yet. Falls back to manual entry when there are no candidates.
 */
export function HsnCodeSelect({ subcategory, value, onChange, containerClassName }) {
  const { data, loading } = useQuery(SUGGEST_HSN, {
    variables: { sub: subcategory },
    skip: !subcategory,
    errorPolicy: 'all',
  });

  const result = data?.hsnCodesBySubcategory;
  const suggested = result?.suggestedHsnCode;

  const options = useMemo(() => {
    const map = new Map();
    const add = (hsn, condition) => {
      const code = hsn?.hsnCode;
      if (!code || map.has(code)) return;
      const rate = gstRate(hsn);
      const label = [code, condition, rate != null ? `GST ${rate}%` : null]
        .filter(Boolean)
        .join(' · ');
      map.set(code, { value: code, label });
    };
    if (result?.primary) {
      add(result.primary, result.primary.hsnCode === suggested ? 'Suggested' : null);
    }
    (result?.candidates || []).forEach((candidate) =>
      add(candidate.hsn, candidate.conditionLabel || (candidate.isDefault ? 'Default' : null)),
    );
    return Array.from(map.values());
  }, [result, suggested]);

  // Auto-apply the suggested code when none is chosen (e.g. after picking the
  // subcategory, which resets the HSN code in the form).
  useEffect(() => {
    if (suggested && !value) onChange(suggested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggested]);

  // Keep the current value selectable even if it isn't among the candidates.
  const allOptions = useMemo(() => {
    if (value && !options.some((option) => option.value === value)) {
      return [{ value, label: value }, ...options];
    }
    return options;
  }, [options, value]);

  if (!subcategory) {
    return (
      <Select
        label="HSN code"
        containerClassName={containerClassName}
        placeholder="Select a subcategory first"
        options={[]}
        value=""
        onChange={() => {}}
        disabled
      />
    );
  }

  if (!loading && options.length === 0) {
    return (
      <Input
        label="HSN code"
        containerClassName={containerClassName}
        placeholder="Enter HSN code"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        hint="No suggestions for this subcategory — enter it manually."
      />
    );
  }

  return (
    <Select
      label="HSN code"
      containerClassName={containerClassName}
      placeholder={loading ? 'Loading suggestions…' : 'Select HSN code'}
      options={allOptions}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      hint="Suggested from the subcategory; used for GST / tax."
    />
  );
}

export default HsnCodeSelect;
